import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import data from './pages.json';
import { canonicalFor, findPage, jsonForScript, routeJsonLd } from './core.mjs';

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content == null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setRouteJsonLd(value) {
  let el = document.head.querySelector('script[data-route-jsonld]');
  if (!value) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-route-jsonld', '');
    document.head.appendChild(el);
  }
  el.textContent = jsonForScript(value);
}

// Keeps <head> in step with client-side navigation. The first load already
// has the right head (scripts/prerender-seo.mjs writes one per public route);
// this covers moving between routes without a reload. Routes not listed in
// pages.json (portal, admin, checkout, unknown paths) are marked noindex.
export default function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = findPage(data, pathname);
    const title = page ? page.title : data.site.defaultTitle;
    const description = page ? page.description : data.site.defaultDescription;
    const canonical = page ? canonicalFor(data, page) : null;
    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', page && !page.noindex ? null : 'noindex');
    setCanonical(canonical);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical || `${data.site.origin}/`);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setRouteJsonLd(page ? routeJsonLd(data, page) : null);
  }, [pathname]);
  return null;
}
